export type DatePrecision = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15

export function normalizeDateString(string: string) {
  const yearLength = string.indexOf('-')
  return string.padStart(4 - yearLength, '0').replace(/Z$/, '')
}

export function parsePrecision(precision: unknown): DatePrecision {
  const numericPrecision = Number(precision)

  if (numericPrecision < 0 || numericPrecision > 15 || !Number.isInteger(numericPrecision)) {
    throw new Error('Precision must be an integer between 0 and 15')
  }

  return numericPrecision as DatePrecision
}

export class DateWithPrecision {
  static readonly PRECISION_BILLION_YEARS = 0
  static readonly PRECISION_HUNDRED_MILLION_YEARS = 1
  static readonly PRECISION_TEN_MILLION_YEARS = 2
  static readonly PRECISION_MILLION_YEARS = 3
  static readonly PRECISION_HUNDRED_THOUSAND_YEARS = 4
  static readonly PRECISION_TEN_THOUSAND_YEARS = 5
  static readonly PRECISION_MILLENIUM = 6
  static readonly PRECISION_CENTURY = 7
  static readonly PRECISION_DECADE = 8
  static readonly PRECISION_YEAR = 9
  static readonly PRECISION_MONTH = 10
  static readonly PRECISION_DAY = 11
  static readonly PRECISION_HOUR = 12
  static readonly PRECISION_MINUTE = 13
  static readonly PRECISION_SECOND = 14
  static readonly PRECISION_MILLISECOND = 15

  #precision: DatePrecision = 15
  #date: Date

  constructor(...args: any[]) {
    this.#date = new Date(...(args as Parameters<DateConstructor>))
  }

  #getEquivalentDate() {
    const date = new Date(
      this.getFullYear(),
      this.getMonth(),
      this.getDate(),
      this.getHours(),
      this.getMinutes(),
      this.getSeconds(),
      this.getMilliseconds(),
    )
    date.setFullYear(this.getFullYear())
    return date
  }

  getPrecision() {
    return this.#precision
  }

  setPrecision(precision: DatePrecision) {
    this.#precision = precision
  }

  getTime() {
    return this.#getEquivalentDate().getTime()
  }

  setTime(time: number) {
    return this.#date.setTime(time)
  }

  getFullYear(): number {
    return this.#precision >= 9 ? this.#date.getFullYear() : 0
  }

  getMonth(): number {
    return this.#precision >= 10 ? this.#date.getMonth() : 0
  }

  getDate(): number {
    return this.#precision >= 11 ? this.#date.getDate() : 0
  }

  getDay(): number {
    return this.#precision >= 11 ? this.#date.getDay() : 0
  }

  getHours(): number {
    return this.#precision >= 12 ? this.#date.getHours() : 0
  }

  getMinutes(): number {
    return this.#precision >= 13 ? this.#date.getMinutes() : 0
  }

  getSeconds(): number {
    return this.#precision >= 14 ? this.#date.getSeconds() : 0
  }

  getMilliseconds(): number {
    return this.#precision >= 15 ? this.#date.getMilliseconds() : 0
  }

  setFullYear(year: number, month?: number | undefined, date?: number | undefined): number {
    if (this.#precision < 9) {
      return this.#date.setFullYear(NaN)
    }

    return this.#date.setFullYear(year, month, date)
  }

  setMonth(month: number, date?: number | undefined): number {
    if (this.#precision < 10) {
      return this.#date.setMonth(NaN)
    }

    return this.#date.setMonth(month, date)
  }

  setDate(date: number): number {
    if (this.#precision < 11) {
      return this.#date.setDate(NaN)
    }

    return this.#date.setDate(date)
  }

  setHours(
    hours: number,
    min?: number | undefined,
    sec?: number | undefined,
    ms?: number | undefined,
  ): number {
    if (this.#precision < 12) {
      return this.#date.setHours(NaN)
    }

    return this.#date.setHours(hours, min, sec, ms)
  }

  setMinutes(min: number, sec?: number | undefined, ms?: number | undefined): number {
    if (this.#precision < 13) {
      return this.#date.setMinutes(NaN)
    }

    return this.#date.setMinutes(min, sec, ms)
  }

  setSeconds(sec: number, ms?: number | undefined): number {
    if (this.#precision < 14) {
      return this.#date.setSeconds(NaN)
    }

    return this.#date.setSeconds(sec, ms)
  }

  setMilliseconds(ms: number): number {
    return this.#date.setMilliseconds(this.#precision < 15 ? NaN : ms)
  }

  getUTCFullYear(): number {
    return this.#precision >= 9 ? this.#date.getUTCFullYear() : 0
  }

  getUTCMonth(): number {
    return this.#precision >= 10 ? this.#date.getUTCMonth() : 0
  }

  getUTCDate(): number {
    return this.#precision >= 11 ? this.#date.getUTCDate() : 0
  }

  getUTCDay(): number {
    return this.#precision >= 11 ? this.#date.getUTCDay() : 0
  }

  getUTCHours(): number {
    return this.#precision >= 12 ? this.#date.getUTCHours() : 0
  }

  getUTCMinutes(): number {
    return this.#precision >= 13 ? this.#date.getUTCMinutes() : 0
  }

  getUTCSeconds(): number {
    return this.#precision >= 14 ? this.#date.getUTCSeconds() : 0
  }

  getUTCMilliseconds(): number {
    return this.#precision >= 15 ? this.#date.getUTCMilliseconds() : 0
  }

  getTimezoneOffset(): number {
    return this.#date.getTimezoneOffset()
  }

  setUTCFullYear(year: number, month?: number | undefined, date?: number | undefined): number {
    if (this.#precision < 10) {
      return this.#date.setUTCFullYear(NaN)
    }

    return this.#date.setUTCFullYear(year, month, date)
  }

  setUTCMonth(month: number, date?: number | undefined): number {
    if (this.#precision < 10) {
      return this.#date.setUTCMonth(NaN)
    }

    return this.#date.setUTCMonth(month, date)
  }

  setUTCDate(date: number): number {
    if (this.#precision < 11) {
      return this.#date.setUTCDate(NaN)
    }

    return this.#date.setUTCDate(date)
  }

  setUTCHours(
    hours: number,
    min?: number | undefined,
    sec?: number | undefined,
    ms?: number | undefined,
  ): number {
    if (this.#precision < 12) {
      return this.#date.setUTCHours(NaN)
    }

    return this.#date.setUTCHours(hours, min, sec, ms)
  }

  setUTCMinutes(min: number, sec?: number | undefined, ms?: number | undefined): number {
    if (this.#precision < 13) {
      return this.#date.setUTCMinutes(NaN)
    }

    return this.#date.setUTCMinutes(min, sec, ms)
  }

  setUTCSeconds(sec: number, ms?: number | undefined): number {
    if (this.#precision < 14) {
      return this.#date.setUTCSeconds(NaN)
    }

    return this.#date.setUTCSeconds(sec, ms)
  }

  setUTCMilliseconds(ms: number): number {
    return this.#date.setUTCMilliseconds(this.#precision < 15 ? NaN : ms)
  }

  toISOString(): string {
    return this.#getEquivalentDate().toISOString()
  }

  toUTCString(): string {
    return this.#getEquivalentDate().toUTCString()
  }

  toDateString(): string {
    return this.#getEquivalentDate().toDateString()
  }

  toTimeString(): string {
    return this.#getEquivalentDate().toTimeString()
  }

  toLocaleString(
    locales?: string | string[] | undefined,
    options?: Intl.DateTimeFormatOptions | undefined,
  ): string {
    return this.#getEquivalentDate().toLocaleString(locales, options)
  }

  toLocaleDateString(
    locales?: string | string[] | undefined,
    options?: Intl.DateTimeFormatOptions | undefined,
  ): string {
    return this.#getEquivalentDate().toLocaleDateString(locales, options)
  }

  toLocaleTimeString(
    locales?: string | string[] | undefined,
    options?: Intl.DateTimeFormatOptions | undefined,
  ): string {
    return this.#getEquivalentDate().toLocaleTimeString(locales, options)
  }

  toJSON(): string {
    return this.#getEquivalentDate().toJSON()
  }

  valueOf(): number {
    return this.getTime()
  }

  toString(): string {
    return this.#getEquivalentDate().toString()
  }

  [Symbol.toPrimitive](hint: 'default'): string
  [Symbol.toPrimitive](hint: 'string'): string
  [Symbol.toPrimitive](hint: 'number'): number
  [Symbol.toPrimitive](hint: 'default' | 'string' | 'number'): string | number {
    return this.#getEquivalentDate()[Symbol.toPrimitive](hint)
  }
}
